import { makeAutoObservable } from "mobx";
import { onError } from "src/common/onError";
import { TableStore } from "src/components/tables/TableStore";
import {
  ZEntityRow,
  EntityFilters,
} from "src/pages/EntityFiltersPage/EntityList/types";
import { t } from "i18next";
import { EntityFiltersPageStore } from "src/pages/EntityFiltersPage/EntityFiltersPageStore";
import { selectOnRowClick } from "src/components/tables/selectOnRowClick";
import { notification } from "antd";
import { ZUid } from "src/types/zUid";
import { bulkPlanCreate, checkBulkPlanCreation } from "./apiBulkPlanCreation";
import {
  ZBulkPlanCreation,
  ZCheckBulkPlanCreation,
} from "./BulkPlanCreationTypes";
import { loadSchedulingSettings } from "../../apiScheduling";
import { SchedRowType } from "../../SchedulingTypes";
import {
  EdBulkPlanCreation,
  edit2BulkPlanCreation,
  ZBulkPlanCreationStep,
} from "./EdBulkPlanCreation";

export type CreationStatus = "blocked" | "canCreate" | "decision";

type CreationSettings = ZBulkPlanCreationStep & {
  filterStore?: EntityFiltersPageStore;
};

export class BulkPlanCreationStore {
  constructor() {
    makeAutoObservable(this);
  }

  tableStore: TableStore<ZEntityRow, EntityFilters> | null = null;

  setTableStore(store: TableStore<ZEntityRow, EntityFilters>) {
    this.tableStore = store;
  }

  objectServiceId: ZUid | null = null;

  setObjectServiceId(id: ZUid) {
    this.objectServiceId = id;
  }

  templatesMap: Record<SchedRowType, ZUid | null | undefined> | null = null;

  setTemplatesMap(map: Record<SchedRowType, ZUid | null | undefined>) {
    this.templatesMap = map;
  }

  open: boolean = false;

  setOpen(flag: boolean) {
    this.open = flag;
  }

  checkData: ZCheckBulkPlanCreation | null = null;

  setCheckData(data: ZCheckBulkPlanCreation | null) {
    this.checkData = data;
  }

  creationSettings: CreationSettings | null = null;

  setCreationSettings(settings: CreationSettings | null) {
    this.creationSettings = settings;
  }

  get creationStatus(): CreationStatus | null {
    const { checkData } = this;
    if (!checkData) return null;
    const { canCreate, entityWithTasks } = checkData;
    if (!canCreate) {
      return "blocked";
    }
    return entityWithTasks.length ? "decision" : "canCreate";
  }

  get taskNames(): string[] {
    return this.checkData?.entityWithTasks.map(({ name }) => name) ?? [];
  }

  get selectedEntities(): ZUid[] | null {
    return this.tableStore?.selected.map(({ id }) => id) ?? null;
  }

  async init(
    tableStore: TableStore<ZEntityRow, EntityFilters>,
    objectServiceId: ZUid,
  ) {
    try {
      this.setTableStore(tableStore);
      this.setObjectServiceId(objectServiceId);
      const schedSettings = await loadSchedulingSettings(objectServiceId);
      this.setTemplatesMap({
        [SchedRowType.plan]:
          schedSettings.planTemplateSettings?.templateObjectId,
        [SchedRowType.group]:
          schedSettings.groupTemplateSettings?.templateObjectId,
        [SchedRowType.task]:
          schedSettings.taskTemplateSettings?.templateObjectId,
      });
    } catch (error) {
      onError(error);
    }
  }

  async checkPlanCreation() {
    const { selectedEntities, objectServiceId } = this;
    try {
      if (!selectedEntities)
        throw Error(t("Missing value", { parameter: "selectedEntities" }));
      if (!objectServiceId)
        throw Error(t("Missing value", { parameter: "objectServiceId" }));
      this.setCheckData(null);
      this.setOpen(true);
      this.setCheckData(
        await checkBulkPlanCreation(objectServiceId, selectedEntities),
      );
    } catch (error) {
      onError(error);
      this.setOpen(false);
    }
  }

  onCLose() {
    this.setOpen(false);
    this.setCheckData(null);
  }

  skipInstances() {
    const { checkData, tableStore } = this;
    try {
      if (!tableStore)
        throw Error(t("Missing value", { parameter: "tableStore" }));
      if (!checkData)
        throw Error(t("Missing value", { parameter: "checkData" }));
      const entitiesWithTasks = new Set<ZUid>(
        checkData?.entityWithTasks.map(({ id }) => id),
      );
      const newSelected = tableStore?.selected.filter(
        ({ id }) => !entitiesWithTasks.has(id),
      );
      tableStore?.setSelected(newSelected);
      this.setCheckData({ ...checkData, entityWithTasks: [] });
    } catch (error) {
      onError(error);
    }
  }

  createTempSelector(values: EdBulkPlanCreation) {
    try {
      const validValues = edit2BulkPlanCreation(values);
      const templateId = this.templatesMap?.[validValues.templateEntityType];
      if (!templateId)
        throw Error(t("Missing value", { parameter: "templateId" }));
      this.setCreationSettings({
        ...validValues,
        filterStore: new EntityFiltersPageStore({
          objectId: templateId,
          selectionSettings: { selectionType: "radio" },
          onRowClick: (row) => {
            const tableStore = this.creationSettings?.filterStore?.tableStore;
            if (!tableStore) return;
            selectOnRowClick(row, tableStore);
          },
        }),
      });
    } catch (error) {
      onError(error);
    }
  }

  async save() {
    const { creationSettings, selectedEntities, objectServiceId, tableStore } =
      this;
    try {
      if (!creationSettings)
        throw Error(t("Missing value", { parameter: "creationSettings" }));
      if (!selectedEntities)
        throw Error(t("Missing value", { parameter: "selectedEntities" }));
      if (!objectServiceId)
        throw Error(t("Missing value", { parameter: "objectServiceId" }));
      const { filterStore, ...rest } = creationSettings;
      const templateEntityId = filterStore?.tableStore?.selected[0]?.id;
      if (!templateEntityId)
        throw Error(t("Missing value", { parameter: "templateEntityId" }));

      const data: ZBulkPlanCreation = {
        ...rest,
        entityIds: selectedEntities,
        templateEntityId,
      };
      await bulkPlanCreate(objectServiceId, data);
      this.setCreationSettings(null);
      this.onCLose();
      notification.success({
        message: t("Calendar plan created"),
      });
      tableStore?.reload();
    } catch (error) {
      onError(error);
    }
  }
}
